import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LumaSpin } from '@/components/ui/luma-spin';
import { PinDots } from '@/components/auth/pin-login/PinDots';
import { PinNumpad } from '@/components/auth/pin-login/PinNumpad';

type GateStatus = 'checking' | 'satisfied' | 'challenge';

interface MfaChallengeGateProps {
  children: React.ReactNode;
}

/**
 * Fecha o gap descoberto em TOTPSettings.tsx: aquele componente só fazia
 * enroll/unenroll do fator TOTP — nada no login checava o segundo fator
 * (escalonamento AAL1→AAL2), então "2FA ativo" era decorativo.
 *
 * Este gate roda DEPOIS que já existe sessão (qualquer método de login — PIN
 * ou email+senha, indiferente) e força o desafio TOTP se o usuário tem um
 * fator verificado e a sessão ainda está em AAL1. Deve envolver as rotas
 * protegidas por fora do ProtectedRoute (mesmo nível de AuthContext).
 *
 * Fail-open deliberado em erro de checagem: MFA aqui é opt-in, não é o
 * boundary de segurança primário (RLS já protege os dados por trás). Um erro
 * transiente na checagem de AAL não deve travar o login de quem nem ativou
 * TOTP.
 */
export function MfaChallengeGate({ children }: MfaChallengeGateProps) {
  const [status, setStatus] = useState<GateStatus>('checking');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const checkAal = useCallback(async () => {
    const { data, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

    if (aalError || !data) {
      console.error('[MfaChallengeGate] aal_check_failed', { message: aalError?.message });
      setStatus('satisfied');
      return;
    }

    if (data.currentLevel === 'aal1' && data.nextLevel === 'aal2') {
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = factorsData?.totp?.find((f) => f.status === 'verified');

      if (factorsError || !verifiedFactor) {
        console.error('[MfaChallengeGate] verified_factor_missing', { message: factorsError?.message });
        setStatus('satisfied');
        return;
      }

      setFactorId(verifiedFactor.id);
      setStatus('challenge');
      return;
    }

    setStatus('satisfied');
  }, []);

  useEffect(() => {
    void checkAal();
  }, [checkAal]);

  const submit = useCallback(
    async (fullCode: string) => {
      if (!factorId) return;
      setVerifying(true);
      setError(null);
      try {
        const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
          factorId,
          code: fullCode,
        });
        if (verifyError) {
          setError('Código inválido. Confira no app autenticador.');
          setCode('');
          return;
        }
        setStatus('satisfied');
      } finally {
        setVerifying(false);
      }
    },
    [factorId],
  );

  useEffect(() => {
    if (code.length === 6 && !verifying) {
      void submit(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleDigit = (digit: string) => {
    if (verifying) return;
    setError(null);
    setCode((prev) => (prev.length < 6 ? prev + digit : prev));
  };

  const handleBackspace = () => {
    if (verifying) return;
    setCode((prev) => prev.slice(0, -1));
  };

  const handleSignOut = () => {
    void supabase.auth.signOut();
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <LumaSpin size="lg" />
      </div>
    );
  }

  if (status === 'challenge') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="space-y-8 w-full max-w-sm">
          <div className="space-y-1 text-center">
            <h1 className="text-xl font-semibold">Verificação em duas etapas</h1>
            <p className="text-sm text-muted-foreground">Digite o código do seu app autenticador</p>
          </div>

          <PinDots length={code.length} error={Boolean(error)} />

          {error && <p className="text-sm text-destructive text-center">{error}</p>}

          <PinNumpad onDigit={handleDigit} onBackspace={handleBackspace} disabled={verifying} />

          <div className="text-center">
            <button
              type="button"
              onClick={handleSignOut}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
